import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogAPI } from '../../services/api';
import { LoadingPage } from '../../components/Loading';
import Pagination from '../../components/Pagination';
import './Public.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await blogAPI.getPosts();
      const data = Array.isArray(response.data) ? response.data : response.data?.results || [];
      // Backend already filters by status='published', so no additional filter needed
      setPosts(data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories
  const categories = ['all', ...new Set(posts.map(p => p.category).filter(Boolean))];

  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = !search ||
      post.title?.toLowerCase().includes(search.toLowerCase()) ||
      post.summary?.toLowerCase().includes(search.toLowerCase()) ||
      post.content?.toLowerCase().includes(search.toLowerCase());
    
    const matchesCategory = category === 'all' || post.category === category;
    
    return matchesSearch && matchesCategory;
  });

  // Sort by date, featured first
  const sortedPosts = [...filteredPosts].sort((a, b) => {
    if (a.is_featured && !b.is_featured) return -1;
    if (!a.is_featured && b.is_featured) return 1;
    return new Date(b.published_date) - new Date(a.published_date);
  });

  // Pagination
  const totalPages = Math.ceil(sortedPosts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPosts = sortedPosts.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, category]);

  if (loading) return <LoadingPage message="載入文章中..." />;

  return (
    <div className="public-page blog-page">
      <div className="page-header">
        <div className="container">
          <h1>最新消息</h1>
          <p className="page-subtitle">掌握最新動態與重要資訊</p>
        </div>
      </div>

      <div className="container">
        {/* Search and Filter */}
        <div className="filter-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="搜尋文章..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>
          {categories.length > 1 && (
            <div className="filter-buttons">
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`filter-btn ${category === cat ? 'active' : ''}`}
                  onClick={() => setCategory(cat)}
                >
                  {cat === 'all' ? '全部分類' : cat}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Blog Grid */}
        {paginatedPosts.length > 0 ? (
          <>
            <div className="blog-grid">
              {paginatedPosts.map((post) => (
                <div key={post.id} className="blog-card card">
                  {post.is_featured && (
                    <span className="badge badge-featured">精選</span>
                  )}
                  {post.featured_image ? (
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="blog-image"
                    />
                  ) : post.video ? (
                    <video
                      src={post.video}
                      className="blog-image"
                      muted
                      playsInline
                      preload="metadata"
                    />
                  ) : null}
                  <div className="blog-content">
                    {post.category && (
                      <span className="category-badge">{post.category}</span>
                    )}
                    <h3>{post.title}</h3>
                    <p className="blog-meta">
                      <span className="blog-date">
                        📅 {new Date(post.published_date).toLocaleDateString('zh-HK', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </span>
                      {post.view_count > 0 && (
                        <span className="blog-views">👁 {post.view_count}</span>
                      )}
                    </p>
                    {post.summary && (
                      <p className="blog-excerpt">{post.summary}</p>
                    )}
                    <Link
                      to={`/blog/${post.id}`}
                      className="btn btn-primary"
                    >
                      閱讀更多
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={sortedPosts.length}
              />
            )}
          </>
        ) : (
          <div className="empty-state">
            <p>沒有找到符合條件的文章</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
